import { Injectable } from '@angular/core';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import type { MapboxMap } from '@polaris-customerweb/sharedcomponents.core';
import { Place, Sponsor } from '@polaris-customerweb/sharedcomponents.map';
import { ConsoleLogService } from '@polaris-customerweb/sharedcomponents.shared';
import type { SourceSpecification, SymbolLayerSpecification } from 'mapbox-gl';
import { GeoJsonFeatureCollection } from 'src/app/models/geojson-feature-collection';
import { MapContentMapService } from './map-content-map.service';
import { MapService } from './map.service';
import { PlaceService } from './place.service';

@Injectable({ providedIn: 'root' })
export class PremiumPoiMapService extends MapContentMapService {
    minZoom = 9;
    layerIdTemp = 'tempPlace';

    private sourceIdTemp = 'tempPlace';
    private tempLayer: SymbolLayerSpecification = {
        id: this.layerIdTemp,
        layout: {
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
            'icon-image': 'poi-waypoint-add',
            'icon-offset': [0, -18],
        },
        source: this.sourceIdTemp,
        type: 'symbol',
    };

    private emptySource: SourceSpecification = {
        data: new GeoJsonFeatureCollection([]),
        type: 'geojson',
    };

    constructor(
        private configService: ConfigService,
        consoleLogService: ConsoleLogService,
        mapService: MapService,
        private placeService: PlaceService
    ) {
        super(consoleLogService, mapService);
        super.setSourceId('sponsors');
    }

    addSources(mapboxMap: MapboxMap) {
        if (!this.configService.featureFlags.areSponsorsEnabled) {
            return;
        }

        super.addSources(mapboxMap);

        this.mapService.addSource(
            mapboxMap,
            this.sourceIdTemp,
            this.emptySource
        );
        this.mapService.insertLayer(mapboxMap, this.tempLayer);
    }

    clearTempSponsor(mapboxMap: MapboxMap): void {
        this.setTempSponsor(mapboxMap, undefined);
    }

    setSourceDataFromModel(mapboxMap: MapboxMap, pointsOfInterest: Place[]) {
        if (!this.configService.featureFlags.areSponsorsEnabled) {
            return;
        }
        const geoJson =
            pointsOfInterest.length > 0
                ? this.placeService.placesToGeoJson(pointsOfInterest)
                : undefined;
        super.setSourceData(mapboxMap, geoJson);
    }

    setTempSponsor(mapboxMap: MapboxMap, sponsor: Sponsor | undefined): void {
        if (!this.configService.featureFlags.areSponsorsEnabled) {
            return;
        }
        if (!sponsor || sponsor.geoCoordinate.isNullLocation) {
            this.mapService.setSourceData(
                mapboxMap,
                this.sourceIdTemp,
                new GeoJsonFeatureCollection()
            );
        } else {
            this.mapService.setSourceData(
                mapboxMap,
                this.sourceIdTemp,
                sponsor.toGeoJson()
            );
        }
    }
}
